/**
 * Builds one batched mesh per system from a bundle, with a stable draw slot
 * per part, and exposes the per-part operations the client's render bridge
 * needs: visibility, colour, level swap.
 */
import { BatchedMesh, BufferAttribute, BufferGeometry, Color, Object3D, type Material } from "three";
import { partArrays, type LevelRange, type Manifest, type PartArrays, type PartRecord, type SystemRecord } from "./format.js";
import { StableBatchedMesh } from "./stable-batched-mesh.js";

export interface BuildOptions {
  /** The level to load into the batches now. */
  level: string;
  /** Systems to build; default every system in the manifest. */
  systems?: string[];
  materialFor: (system: SystemRecord) => Material;
  /** `false` builds plain `BatchedMesh`es (the flicker A/B in the harness). Default true. */
  stable?: boolean;
  /**
   * Merge adjacent slots that share geometry adjacency and instance data into one
   * sub-draw. Default true; `false` is the A/B for the frame-time cost of drawing
   * every part on its own. Ignored when `stable` is false.
   */
  mergeRuns?: boolean;
  /** Reserve capacity for these levels so `addLevel` can add them later. Default: `[level]`. */
  reserveLevels?: string[];
  /** Tint each instance with its system colour. Default true. */
  colourInstances?: boolean;
}

export interface AtlasPart {
  record: PartRecord;
  system: string;
  mesh: BatchedMesh;
  instanceId: number;
  geometryIds: Map<string, number>;
  currentLevel: string;
}

export interface AtlasStats {
  systems: number;
  instances: number;
  triangles: number;
  culled: number;
  /**
   * Sub-draws submitted for the last frame. `renderer.info.render.calls` counts
   * a whole multi-draw as one call, so it never showed the ~2,200 sub-draws that
   * session 2 measured as the frame-time cost; this is the number that moves.
   */
  runs: number;
}

export class BatchedAtlas {
  readonly root = new Object3D();
  readonly meshes = new Map<string, BatchedMesh>();
  readonly parts = new Map<string, AtlasPart>();
  /**
   * Instance slot back to the part in it, per mesh. `BatchedMesh.raycast`
   * reports which instance a ray hit as `batchId`, and this is what turns that
   * number into a part the client can name and select.
   */
  private readonly slots = new Map<BatchedMesh, AtlasPart[]>();
  /** System colour per part, so a highlight can be undone without a lookup table in the client. */
  private readonly baseColour = new Map<string, string>();
  private readonly colour = new Color();

  constructor(readonly manifest: Manifest) {
    this.root.name = manifest.id;
  }

  setVisible(partId: string, visible: boolean): void {
    const part = this.parts.get(partId);
    if (part) part.mesh.setVisibleAt(part.instanceId, visible);
  }

  setColour(partId: string, colour: string | number): void {
    const part = this.parts.get(partId);
    if (part) part.mesh.setColorAt(part.instanceId, this.colour.set(colour));
  }

  /** Put a part back to its system's colour, undoing a highlight. */
  resetColour(partId: string): void {
    const base = this.baseColour.get(partId);
    if (base !== undefined) this.setColour(partId, base);
  }

  /** Record a part's slot so a raycast hit can be resolved back to it. */
  register(part: AtlasPart, colour: string): void {
    this.parts.set(part.record.id, part);
    this.baseColour.set(part.record.id, colour);
    let list = this.slots.get(part.mesh);
    if (!list) this.slots.set(part.mesh, (list = []));
    list[part.instanceId] = part;
  }

  /** The part a `BatchedMesh.raycast` hit landed on, or undefined. */
  partAt(mesh: BatchedMesh, instanceId: number): AtlasPart | undefined {
    return this.slots.get(mesh)?.[instanceId];
  }

  /** Add a level's geometry for a part (capacity must have been reserved). */
  addLevel(partId: string, level: string, arrays: PartArrays): void {
    const part = this.parts.get(partId);
    if (!part || part.geometryIds.has(level)) return;
    const geometryId = part.mesh.addGeometry(geometryFrom(arrays));
    part.geometryIds.set(level, geometryId);
    if (part.mesh instanceof StableBatchedMesh) part.mesh.ensureBoundsTree(geometryId);
  }

  /**
   * Build the raycast acceleration trees for every system now. Synchronous:
   * the whole body takes a few hundred milliseconds on a desk, so the client
   * uses `buildBoundsTreesIdle` and this is for tests and tools.
   */
  buildBoundsTrees(): void {
    for (const mesh of this.meshes.values()) if (mesh instanceof StableBatchedMesh) mesh.buildBoundsTrees();
  }

  /**
   * Build the trees one system per idle slice, largest systems first, so a
   * headset never pauses after the body appears. A raycast that reaches a
   * system before its tree exists takes the slow path once and is still
   * correct. Returns a cancel function; `boundsTreeStatus` reports progress.
   */
  buildBoundsTreesIdle(): () => void {
    const pending: StableBatchedMesh[] = [];
    for (const mesh of this.meshes.values()) if (mesh instanceof StableBatchedMesh && !mesh.boundsTrees) pending.push(mesh);
    pending.sort((a, b) => b.geometry.index!.count - a.geometry.index!.count);
    let cancelled = false;
    // Looked up on globalThis: the pipeline compiles this file without the DOM
    // library, and a host without idle callbacks (a test, a worker) gets a timer.
    const idleCallback = (globalThis as { requestIdleCallback?: (fn: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    const idle: (fn: () => void) => void = typeof idleCallback === "function" ? (fn) => idleCallback(() => fn(), { timeout: 250 }) : (fn) => setTimeout(fn, 16);
    const step = (): void => {
      if (cancelled) return;
      const mesh = pending.shift();
      if (!mesh) return;
      if (!mesh.boundsTrees) mesh.buildBoundsTrees();
      if (pending.length) idle(step);
    };
    idle(step);
    return () => {
      cancelled = true;
    };
  }

  /** Free the raycast trees (a body being removed). */
  disposeBoundsTrees(): void {
    for (const mesh of this.meshes.values()) if (mesh instanceof StableBatchedMesh) mesh.disposeBoundsTrees();
  }

  /** How many systems have their raycast trees, out of how many could. */
  boundsTreeStatus(): { built: number; total: number } {
    let built = 0;
    let total = 0;
    for (const mesh of this.meshes.values()) {
      if (!(mesh instanceof StableBatchedMesh)) continue;
      total += 1;
      if (mesh.boundsTrees) built += 1;
    }
    return { built, total };
  }

  /** Swap a part to a loaded level. Returns false if the level is not loaded. */
  setLevel(partId: string, level: string): boolean {
    const part = this.parts.get(partId);
    const geometryId = part?.geometryIds.get(level);
    if (!part || geometryId === undefined) return false;
    if (part.currentLevel !== level) {
      part.mesh.setGeometryIdAt(part.instanceId, geometryId);
      part.currentLevel = level;
    }
    return true;
  }

  stats(): AtlasStats {
    let instances = 0;
    let triangles = 0;
    let culled = 0;
    for (const part of this.parts.values()) {
      instances += 1;
      const range = part.record.levels[part.currentLevel];
      if (range && part.mesh.getVisibleAt(part.instanceId)) triangles += range.tris;
    }
    let runs = 0;
    for (const mesh of this.meshes.values()) {
      if (mesh instanceof StableBatchedMesh) {
        culled += mesh.lastCulled;
        runs += mesh.lastRuns;
      } else {
        // Stock BatchedMesh compacts its draw list, so its entry count is its sub-draw count.
        runs += (mesh as unknown as { _multiDrawCount: number })._multiDrawCount ?? 0;
      }
    }
    return { systems: this.meshes.size, instances, triangles, culled, runs };
  }
}

export function geometryFrom(arrays: PartArrays): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(arrays.positions, 3));
  geometry.setAttribute("normal", new BufferAttribute(arrays.normals, 3, true));
  geometry.setIndex(new BufferAttribute(arrays.indices, 1));
  return geometry;
}

/**
 * Build the batches. Parts are added in manifest order within a system, so
 * instance i is always slot i (the stable-slot invariant); systems draw
 * outer-to-inner through `renderOrder = layer`.
 */
export function buildBatchedAtlas(manifest: Manifest, chunks: Map<number, ArrayBuffer>, opts: BuildOptions): BatchedAtlas {
  const atlas = new BatchedAtlas(manifest);
  const wanted = new Set(opts.systems ?? manifest.systems.map((s) => s.id));
  const reserve = opts.reserveLevels ?? [opts.level];
  const stable = opts.stable ?? true;
  const colourInstances = opts.colourInstances ?? true;
  const systems = new Map(manifest.systems.map((s) => [s.id, s]));

  const bySystem = new Map<string, PartRecord[]>();
  for (const part of manifest.parts) {
    if (!wanted.has(part.system) || !part.levels[opts.level]) continue;
    let list = bySystem.get(part.system);
    if (!list) bySystem.set(part.system, (list = []));
    list.push(part);
  }

  for (const [systemId, parts] of bySystem) {
    const system = systems.get(systemId);
    if (!system) continue;
    let vertices = 0;
    let indices = 0;
    for (const part of parts) {
      for (const level of reserve) {
        const range = part.levels[level];
        if (range) {
          vertices += range.v;
          indices += range.i;
        }
      }
    }
    const material = opts.materialFor(system);
    let mesh: BatchedMesh;
    if (stable) {
      const batch = new StableBatchedMesh(parts.length, vertices, indices, material);
      batch.mergeRuns = opts.mergeRuns ?? true;
      // `customSort` is typed on `this`, so the subclass is not assignable without it
      mesh = batch as unknown as BatchedMesh;
    } else {
      mesh = new BatchedMesh(parts.length, vertices, indices, material);
    }
    mesh.name = `atlas:${systemId}`;
    mesh.renderOrder = system.layer;
    for (const part of parts) {
      const range = part.levels[opts.level] as LevelRange;
      const buffer = chunks.get(range.chunk);
      if (!buffer) throw new Error(`chunk ${range.chunk} for ${part.id} (${opts.level}) was not loaded`);
      const geometryId = mesh.addGeometry(geometryFrom(partArrays(buffer, range)));
      const instanceId = mesh.addInstance(geometryId);
      if (colourInstances) mesh.setColorAt(instanceId, new Color(system.colour));
      atlas.register({ record: part, system: systemId, mesh, instanceId, geometryIds: new Map([[opts.level, geometryId]]), currentLevel: opts.level }, system.colour);
    }
    atlas.meshes.set(systemId, mesh);
    atlas.root.add(mesh);
  }
  return atlas;
}
